import { EMPTY_SITE_FILTER, type SiteFilterQuery, type SitePage, type ToggleKey } from "../../api/sites";

/** The register's filter lives in the URL, so a filtered list is a link --
 * the dashboard's tiles, the tracker, the masthead search (`/sites?q=…`) and
 * the Jinja pages all point at `/sites?…`, and those links keep meaning the
 * same thing. The page number rides along as `page`, which is not a filter:
 * changing any filter writes a fresh query without it, back to the first page. */

export const PAGE_SIZE = 25;

export function filterFromSearch(params: URLSearchParams): SiteFilterQuery {
  const filter: SiteFilterQuery = { ...EMPTY_SITE_FILTER, status: params.getAll("status") };
  for (const key of Object.keys(EMPTY_SITE_FILTER) as (keyof SiteFilterQuery)[]) {
    if (key !== "status") {
      filter[key] = params.get(key) ?? "";
    }
  }
  return filter;
}

export function searchFromFilter(filter: SiteFilterQuery): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (key === "status") {
      for (const status of value as string[]) {
        params.append("status", status);
      }
    } else if (value !== "") {
      params.set(key, value as string);
    }
  }
  return params;
}

/** The zero-based page the URL asks for; anything unreadable is the first. */
export function pageFromSearch(params: URLSearchParams): number {
  const page = Number(params.get("page") ?? "1");
  return Number.isInteger(page) && page >= 1 ? page - 1 : 0;
}

export function withPage(params: URLSearchParams, page: number): URLSearchParams {
  const next = new URLSearchParams(params);
  if (page <= 0) {
    next.delete("page");
  } else {
    next.set("page", String(page + 1));
  }
  return next;
}

export function pageRequest(page: number): SitePage {
  return { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
}

/** "1–25 of 371 sites"; an empty filter says so rather than "1–0 of 0". */
export function rangeText(offset: number, shown: number, total: number): string {
  if (total === 0) {
    return "0 sites";
  }
  const noun = total === 1 ? "site" : "sites";
  return `${offset + 1}–${offset + shown} of ${total} ${noun}`;
}

/** The quick toggles: each is one filter field set to one value, so ticking
 * one is an ordinary filter change and its address is an ordinary link. */
export interface Toggle {
  key: ToggleKey;
  label: string;
  field: keyof Omit<SiteFilterQuery, "status">;
  value: string;
}

export const TOGGLES: readonly Toggle[] = [
  { key: "flagged", label: "Flagged", field: "flagged", value: "on" },
  { key: "red_list", label: "Red List", field: "red_list", value: "on" },
  { key: "not_located", label: "Not located", field: "not_located", value: "on" },
  { key: "no_qcp", label: "No QCP", field: "no_qcp", value: "on" },
  { key: "deferred", label: "Deferred", field: "deferred", value: "on" },
  // The last planned visit was not done, and the site is still owed one.
  { key: "not_done", label: "Visit not done", field: "not_done", value: "on" },
  { key: "unassigned", label: "Unassigned", field: "assigned", value: "unassigned" },
];

export function isToggleOn(filter: SiteFilterQuery, toggle: Toggle): boolean {
  return filter[toggle.field] === toggle.value;
}

/** Whether anything narrows the list, which is when "Clear all" is offered. */
export function isFiltered(filter: SiteFilterQuery): boolean {
  return searchFromFilter(filter).toString() !== "";
}
